import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformAuditService } from '@/services/platformAuditService';

export type CompanyStatus = 'pending_approval' | 'active' | 'suspended' | 'banned';
export type DocumentStatus = 'unverified' | 'pending' | 'verified';

export interface AdminCompany {
  id: string;
  name: string;
  cnpj: string | null;
  razao_social: string | null;
  tenant_status: CompanyStatus;
  document_status: DocumentStatus;
  created_at: string;
  approved_at: string | null;
  suspended_at: string | null;
  suspended_reason: string | null;
  responsible_email: string | null;
  responsible_name: string | null;
  user_count: number;
  subscription_plan: string | null;
  active_modules: string[] | null;
}

interface PlatformAdminContextType {
  isPlatformAdmin: boolean;
  isCheckingAdmin: boolean;
  companies: AdminCompany[];
  isLoadingCompanies: boolean;
  fetchCompanies: () => Promise<void>;
  approveCompany: (companyId: string) => Promise<boolean>;
  suspendCompany: (companyId: string, reason: string) => Promise<boolean>;
  reactivateCompany: (companyId: string) => Promise<boolean>;
  stats: {
    total: number;
    pending: number;
    active: number;
    suspended: number;
  };
}

const PlatformAdminContext = createContext<PlatformAdminContextType | undefined>(undefined);

export const usePlatformAdmin = () => {
  const context = useContext(PlatformAdminContext);
  if (context === undefined) {
    // Graceful fallback: retorna valores seguros em vez de lançar erro.
    // isCheckingAdmin: true faz o ProtectedRoute mostrar o spinner de loading
    // até o PlatformAdminProvider terminar de montar.
    return {
      isPlatformAdmin: false,
      isCheckingAdmin: true,
      companies: [] as AdminCompany[],
      isLoadingCompanies: false,
      fetchCompanies: async () => {},
      approveCompany: async () => false,
      suspendCompany: async (_id: string, _reason: string) => false,
      reactivateCompany: async () => false,
      stats: { total: 0, pending: 0, active: 0, suspended: 0 },
    } as PlatformAdminContextType;
  }
  return context;
};

interface PlatformAdminProviderProps {
  children: ReactNode;
}

export const PlatformAdminProvider = ({ children }: PlatformAdminProviderProps) => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const { toast } = useToast();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Verificar se usuário é Platform Admin
  const checkPlatformAdmin = useCallback(async () => {
    if (!user) {
      setIsPlatformAdmin(false);
      setIsCheckingAdmin(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('is_platform_admin', { 
        check_user_id: user.id 
      });

      if (error) {
        console.error('Erro ao verificar platform admin:', error);
        setIsPlatformAdmin(false);
      } else {
        setIsPlatformAdmin(data === true);
      }
    } catch (error) {
      console.error('Erro ao verificar platform admin:', error);
      setIsPlatformAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  }, [user]);

  useEffect(() => {
    checkPlatformAdmin();
  }, [checkPlatformAdmin]);

  // Buscar todas as empresas
  const fetchCompanies = useCallback(async () => {
    if (!isPlatformAdmin) return;

    setIsLoadingCompanies(true);
    try {
      const { data, error } = await supabase.rpc('get_all_companies_admin');

      if (error) {
        console.error('Erro ao buscar empresas:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar a lista de empresas',
          variant: 'destructive',
        });
        return;
      }

      // Cast the response to the expected type
      const typedData = (data || []) as unknown as AdminCompany[];
      setCompanies(typedData);
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [isPlatformAdmin, toast]);

  // Aprovar empresa
  const approveCompany = useCallback(async (companyId: string): Promise<boolean> => {
    try {
      const company = companies.find(c => c.id === companyId);
      const { data, error } = await supabase.rpc('approve_company', { 
        target_company_id: companyId 
      });

      if (error) throw error;

      const result = data as { success: boolean; message?: string; error?: string };
      
      if (result.success) {
        // Log de auditoria
        await platformAuditService.logAction({
          action: 'APPROVE_COMPANY',
          targetEntityType: 'company',
          targetCompanyId: companyId,
          description: `Empresa ${company?.name || companyId} aprovada`,
          details: { company_name: company?.name, cnpj: company?.cnpj }
        });

        // Enviar email de notificação
        supabase.functions.invoke('notify-company-status-change', {
          body: { type: 'approved', company_id: companyId, company_name: company?.name || '' }
        }).catch(err => console.error('Erro ao enviar email:', err));

        toast({
          title: 'Sucesso',
          description: result.message || 'Empresa aprovada com sucesso',
        });
        await fetchCompanies();
        return true;
      } else {
        throw new Error(result.error || 'Erro ao aprovar empresa');
      }
    } catch (error: any) {
      console.error('Erro ao aprovar empresa:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível aprovar a empresa',
        variant: 'destructive',
      });
      return false;
    }
  }, [companies, fetchCompanies, toast]);

  // Suspender empresa
  const suspendCompany = useCallback(async (companyId: string, reason: string): Promise<boolean> => {
    try {
      const company = companies.find(c => c.id === companyId);
      const { data, error } = await supabase.rpc('suspend_company', { 
        target_company_id: companyId,
        reason: reason
      });

      if (error) throw error;

      const result = data as { success: boolean; message?: string; error?: string };
      
      if (result.success) {
        // Log de auditoria
        await platformAuditService.logAction({
          action: 'SUSPEND_COMPANY',
          targetEntityType: 'company',
          targetCompanyId: companyId,
          description: `Empresa ${company?.name || companyId} suspensa`,
          details: { company_name: company?.name, reason }
        });

        // Enviar email de notificação
        supabase.functions.invoke('notify-company-status-change', {
          body: { type: 'suspended', company_id: companyId, company_name: company?.name || '', reason }
        }).catch(err => console.error('Erro ao enviar email:', err));

        toast({
          title: 'Sucesso',
          description: result.message || 'Empresa suspensa com sucesso',
        });
        await fetchCompanies();
        return true;
      } else {
        throw new Error(result.error || 'Erro ao suspender empresa');
      }
    } catch (error: any) {
      console.error('Erro ao suspender empresa:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível suspender a empresa',
        variant: 'destructive',
      });
      return false;
    }
  }, [companies, fetchCompanies, toast]);

  // Reativar empresa
  const reactivateCompany = useCallback(async (companyId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('reactivate_company', { 
        target_company_id: companyId 
      });

      if (error) throw error;

      const result = data as { success: boolean; message?: string; error?: string };
      
      if (result.success) {
        toast({
          title: 'Sucesso',
          description: result.message || 'Empresa reativada com sucesso',
        });
        await fetchCompanies();
        return true;
      } else {
        throw new Error(result.error || 'Erro ao reativar empresa');
      }
    } catch (error: any) {
      console.error('Erro ao reativar empresa:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível reativar a empresa',
        variant: 'destructive',
      });
      return false;
    }
  }, [fetchCompanies, toast]);

  // Estatísticas
  const stats = {
    total: companies.length,
    pending: companies.filter(c => c.tenant_status === 'pending_approval').length,
    active: companies.filter(c => c.tenant_status === 'active').length,
    suspended: companies.filter(c => c.tenant_status === 'suspended' || c.tenant_status === 'banned').length,
  };

  return (
    <PlatformAdminContext.Provider
      value={{
        isPlatformAdmin,
        isCheckingAdmin,
        companies,
        isLoadingCompanies,
        fetchCompanies,
        approveCompany,
        suspendCompany,
        reactivateCompany,
        stats,
      }}
    >
      {children}
    </PlatformAdminContext.Provider>
  );
};
